import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, User, ArrowRight, Shield, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OwnerSetupPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  
  // Setup form
  const [setupForm, setSetupForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: ""
  });

  useEffect(() => {
    // Check if setup is still required
    const checkSetup = async () => {
      try {
        const response = await axios.get(`${API}/owner/check-setup`);
        if (!response.data.setup_required) {
          // Owner already exists, redirect to login
          navigate("/owner-login");
          return;
        }
      } catch (error) {
        console.error("Error checking setup:", error);
      } finally {
        setLoading(false);
      }
    };

    checkSetup();
  }, [navigate]);

  const handleSetup = async (e) => {
    e.preventDefault();
    
    if (setupForm.password !== setupForm.confirm_password) {
      toast.error("Passwords do not match.");
      return;
    }

    if (setupForm.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }

    setSubmitting(true);

    try {
      // Create the owner account
      await axios.post(`${API}/owner/setup`, setupForm);
      
      setSetupComplete(true);
      toast.success("Account created successfully!");

      // Auto-login after setup
      setTimeout(async () => {
        try {
          const loginResponse = await axios.post(`${API}/owner/login`, {
            email: setupForm.email,
            password: setupForm.password
          });
          localStorage.setItem("owner_token", loginResponse.data.access_token);
          navigate("/owner-dashboard");
        } catch (e) {
          navigate("/owner-login");
        }
      }, 1500);
    } catch (error) {
      console.error("Setup error:", error);
      toast.error(error.response?.data?.detail || "Failed to create account.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Checking setup status...</p>
        </div>
      </div>
    );
  }

  if (setupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Account Created!</h1>
          <p className="text-gray-600 mb-4">Redirecting to your dashboard...</p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="owner-setup-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Create Owner Account
            </h1>
            <p className="text-gray-600">
              Set up your admin account to manage DC Tiling & Bathrooms
            </p>
          </div>

          {/* Welcome Message */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Welcome!</strong> This is the first-time setup. Create your owner credentials to access the control panel.
            </p>
          </div>

          {/* Setup Form */}
          <form onSubmit={handleSetup} className="space-y-4">
            <div>
              <Label htmlFor="name">Your Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="name"
                  type="text"
                  value={setupForm.name}
                  onChange={(e) => setSetupForm({ ...setupForm, name: e.target.value })}
                  placeholder="Enter your name"
                  className="pl-10 h-12"
                  required
                  data-testid="setup-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="setup-email">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="setup-email"
                  type="email"
                  value={setupForm.email}
                  onChange={(e) => setSetupForm({ ...setupForm, email: e.target.value })}
                  placeholder="your@email.com"
                  className="pl-10 h-12"
                  required
                  data-testid="setup-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="setup-password">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="setup-password"
                  type={showPassword ? "text" : "password"}
                  value={setupForm.password}
                  onChange={(e) => setSetupForm({ ...setupForm, password: e.target.value })}
                  placeholder="Minimum 8 characters"
                  className="pl-10 pr-10 h-12"
                  required
                  data-testid="setup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  value={setupForm.confirm_password}
                  onChange={(e) => setSetupForm({ ...setupForm, confirm_password: e.target.value })}
                  placeholder="Confirm your password"
                  className="pl-10 h-12"
                  required
                  data-testid="setup-confirm-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-primary hover:bg-primary/90"
              disabled={submitting}
              data-testid="setup-submit-btn"
            >
              {submitting ? "Creating Account..." : "Create Account & Continue"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          {/* Back to site link */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-gray-500 hover:text-primary">
              ← Back to DC Tiling & Bathrooms website
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerSetupPage;
